import { ChangeEvent, Dispatch, ReactNode, SetStateAction, useCallback, useState } from 'react';
import { clsx } from 'clsx';

import { PingInput, ScrollableTab, Spinner, Table } from '@ping/uikit';
import { useAssetContractStatus, useIsUserLoggedIn, useMobile } from '@ping/hooks';
import { parsePair } from '@ping/helpers';
import { marketStore } from '@ping/stores/market.store';
import { marketPairsModalRemoteStateSelector, useModalStore } from '@ping/stores/modal.store';
import { AssetContractSignModal } from '@ping/components/AssetContractSignModal';
import { getMarketPairsColumns } from '../helpers';

import SearchIcon from '@ping/assets/Icon/search.svg';

import style from './style.module.scss';

interface IMarketPairsProps {
  visiblePairs: [string, IMarketModified[]][];
  handleSearch: (ev: ChangeEvent<HTMLInputElement>) => void;
  showMarket: string;
  coinTabOptions: {
    value: string;
    view: ReactNode;
  }[];
  selectedMarket: string;
  filterPairs: (pairs: any, showMarket: string) => any;
  filterQuery: string;
  setShowMarket: Dispatch<SetStateAction<string>>;
  /** optional setter used only for filter tabs so they don't trigger side-effects like closing dropdowns */
  setShowMarketForFilter?: Dispatch<SetStateAction<string>>;
  setFilterQuery?: Dispatch<SetStateAction<string>>;
}

export const MarketPairs = ({
  visiblePairs,
  showMarket,
  setShowMarket,
  coinTabOptions,
  filterPairs,
  handleSearch,
  filterQuery,
  setShowMarketForFilter,
  setFilterQuery,
  selectedMarket,
}: IMarketPairsProps) => {
  const isUserLoggedIn = useIsUserLoggedIn();
  const isMobile = useMobile();
  const marketPairsModal = useModalStore(marketPairsModalRemoteStateSelector);
  const { isContractSigned, getAssetContract } = useAssetContractStatus();
  const [contractModalAsset, setContractModalAsset] = useState<{
    assetId: string;
    assetName: string;
  } | null>(null);

  const handleClickRow = useCallback(
    row => {
      const market: string = row.original.market;
      const base = market.split('/')[0]?.toUpperCase();

      if (base && !isContractSigned(base)) {
        const assetContract = getAssetContract(base);
        setContractModalAsset({
          assetId: assetContract?.assetId || base,
          assetName: assetContract?.assetName || base,
        });
        return;
      }

      /* reset search input after market change */
      setFilterQuery?.('');

      const nextShowMarket = market.toLowerCase().split('/')[0];
      /* for selection, always use main setShowMarket so any caller-specific side-effects (like closing dropdown) still run */
      setShowMarket(nextShowMarket);

      marketStore.setSelectedMarket(market);
      /* always close the pair selection modal (if open) after selecting a market */
      marketPairsModal.close();
    },
    [isContractSigned, getAssetContract, marketPairsModal, setFilterQuery, setShowMarket]
  );

  return (
    <div className={clsx(style['market-pairs'], visiblePairs.length !== 0 && style['hover-effect'])}>
      {showMarket && visiblePairs.length !== 0 && (
        <>
          <PingInput value={filterQuery} onChange={handleSearch} placeholder='Search' Icon={<SearchIcon />} />
          {!isMobile && (
            <ScrollableTab
              className={style['market-pairs-filter-options']}
              options={coinTabOptions}
              value={showMarket}
              onChange={market => (setShowMarketForFilter ?? setShowMarket)(market as string)}
            />
          )}

          {filterPairs(visiblePairs, showMarket).map(([coinKey, coinData]) => (
            <div key={coinKey} className={style['market-pairs__table-container']}>
              <Table
                data={coinData.filter(coin => coin?.keywords.some(key => key.includes(filterQuery)))}
                columns={getMarketPairsColumns(isUserLoggedIn, isContractSigned)}
                isLoading={!visiblePairs.length}
                isMobileStickyColumn={false}
                getRowProps={row => ({
                  className:
                    selectedMarket === parsePair.byInstrument(row.original.instrument).market && style['selected-row'],
                  onClick: () => handleClickRow(row),
                })}
              />
            </div>
          ))}
        </>
      )}

      {visiblePairs.length === 0 && (
        <>
          <div className='text-center my-3'>
            <Spinner size={10} />
          </div>
          <div className={style['market-pairs__table-container']}>
            <Table
              columns={getMarketPairsColumns(isUserLoggedIn)}
              data={[]}
              isLoading={!visiblePairs.length}
              isMobileStickyColumn={false}
              loadingItemsPerPage={18}
            />
          </div>
        </>
      )}

      <AssetContractSignModal
        isOpen={!!contractModalAsset}
        onClose={() => setContractModalAsset(null)}
        assetId={contractModalAsset?.assetId || ''}
        assetName={contractModalAsset?.assetName || ''}
        onSigned={() => setContractModalAsset(null)}
      />
    </div>
  );
};
